import React, { useState, useRef } from "react";
import { Plus, X, Edit2, Trash2, Boxes, Upload, FileUp } from "lucide-react";
import { toast } from "sonner";
import { PageHead } from "../components/PageHead";
import { DataTable } from "../components/DataTable";
import { StockTypeahead } from "../components/StockTypeahead";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";

// PTE-STOCK-0001 — a few common units as a baseline so the dropdown isn't
// empty before any stock item has ever used them; merged with whatever units
// are already in use (see UNIT_OPTIONS load in StockModal).
const UNIT_DEFAULTS = ["EA", "Litre", "Kilogram", "Metre", "Set", "Box",
  "Roll", "Pair", "Package of 10", "Package of 20", "Package of 50"];

// Standard currency list (ISO 4217) — ZAR/USD/EUR/CNY first since those are
// PTE's most common, plus the other majors + regional African currencies.
const CURRENCIES = [
  ["ZAR", "South African Rand"], ["USD", "US Dollar"], ["EUR", "Euro"], ["CNY", "Chinese Yuan"],
  ["GBP", "British Pound"], ["AUD", "Australian Dollar"], ["CAD", "Canadian Dollar"], ["CHF", "Swiss Franc"],
  ["JPY", "Japanese Yen"], ["INR", "Indian Rupee"], ["AED", "UAE Dirham"], ["BWP", "Botswana Pula"],
  ["ZMW", "Zambian Kwacha"], ["NAD", "Namibian Dollar"], ["MZN", "Mozambican Metical"], ["KES", "Kenyan Shilling"],
  ["NGN", "Nigerian Naira"], ["EGP", "Egyptian Pound"], ["SGD", "Singapore Dollar"], ["HKD", "Hong Kong Dollar"],
  ["NZD", "New Zealand Dollar"], ["SEK", "Swedish Krona"], ["NOK", "Norwegian Krone"], ["DKK", "Danish Krone"],
  ["BRL", "Brazilian Real"], ["MXN", "Mexican Peso"], ["KRW", "South Korean Won"], ["THB", "Thai Baht"],
];

// All editable fields (keeps every column from the Caspio NewStockList)
// `kind` drives the input widget rendered for each field — see StockModal.
const FIELDS = [
  { key: "part_number", label: "Part number", kind: "typeahead", placeholder: "e.g. 9X6857" },
  { key: "description", label: "Description", textarea: true },
  { key: "keyword", label: "Keyword", kind: "typeahead" },
  { key: "alt_pn", label: "Alternate part no.", kind: "typeahead" },
  { key: "nha_pn", label: "NHA part no.", kind: "typeahead" },
  { key: "bin", label: "Bin" },
  { key: "store", label: "Store", kind: "typeahead" },
  { key: "qty", label: "Qty", type: "number" },
  { key: "unit", label: "Unit", kind: "unit" },
  { key: "price", label: "Price", type: "number", step: "0.0001" },
  { key: "currency", label: "Currency", kind: "currency" },
  { key: "supplier", label: "Supplier", kind: "typeahead" },
  { key: "date_po", label: "PO date", type: "date" },
  { key: "kit_list", label: "Kit list" },
  { key: "book_lib_ref", label: "Book / library ref." },
  { key: "doc_ref", label: "Document ref." },
  { key: "doc_date", label: "Document date", type: "date" },
];

const EMPTY = FIELDS.reduce((a, f) => ({ ...a, [f.key]: "" }), {});

// SPT-PTE-2026-0070 - new items default to Rand + Each; existing items with
// no currency show (and save) ZAR. Stray quotes from the Caspio import
// ('ZAR') are stripped so the value matches the dropdown.
const DEFAULT_CURRENCY = "ZAR";
const DEFAULT_UNIT = "EA";
const NEW_ITEM = { ...EMPTY, currency: DEFAULT_CURRENCY, unit: DEFAULT_UNIT };
const cleanCurrency = (v) => String(v ?? "").trim().replace(/^['"]+|['"]+$/g, "").trim().toUpperCase();
const formFrom = (initial) => (initial
  ? { ...EMPTY, ...initial, currency: cleanCurrency(initial.currency) || DEFAULT_CURRENCY }
  : NEW_ITEM);

function StockModal({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState(formFrom(initial));
  const [busy, setBusy] = useState(false);
  const [unitOptions, setUnitOptions] = useState(UNIT_DEFAULTS);
  React.useEffect(() => { if (open) setForm(formFrom(initial)); }, [open, initial]);
  React.useEffect(() => {
    if (!open) return;
    api.get("/stock-items/lookup", { params: { field: "unit", q: "" } })
      .then((r) => {
        const existing = (r.data?.matches || []).map((m) => m.value);
        setUnitOptions([...new Set([...existing, ...UNIT_DEFAULTS])]);
      })
      .catch(() => setUnitOptions(UNIT_DEFAULTS));
  }, [open]);
  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!String(form.part_number || "").trim() && !String(form.description || "").trim())
      return toast.error("Enter at least a part number or description.");
    setBusy(true);
    try {
      const payload = { ...form };
      payload.qty = form.qty === "" || form.qty == null ? 0 : Number(form.qty);
      payload.price = form.price === "" || form.price == null ? 0 : Number(form.price);
      if (initial?.id) {
        await api.patch(`/stock-items/${initial.id}`, payload);
        toast.success("Stock item updated");
      } else {
        await api.post("/stock-items", payload);
        toast.success("Stock item added");
      }
      onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const unitSelectOptions = form.unit && !unitOptions.includes(form.unit)
    ? [form.unit, ...unitOptions] : unitOptions;
  const currencySelectOptions = form.currency && !CURRENCIES.some(([c]) => c === form.currency)
    ? [[form.currency, form.currency], ...CURRENCIES] : CURRENCIES;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()} className="card pad"
        style={{ width: "min(820px, 100%)", maxHeight: "88vh", overflowY: "auto", background: "var(--card)" }}
        data-testid="stock-modal">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <Boxes size={18} color="var(--gold-deep)" />
            {initial?.id ? "Edit stock item" : "Add stock item"}
          </h3>
          <button className="btn ghost sm" onClick={onClose}><X size={14} /></button>
        </div>
        {initial?.legacy_id && (
          <div className="t-mut" style={{ fontSize: 11.5, marginBottom: 10 }}>Caspio ID: <span className="mono">{initial.legacy_id}</span></div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {FIELDS.map((f) => (
            <div key={f.key} className="fld" style={{ gridColumn: f.textarea ? "1 / -1" : undefined }}>
              <label>{f.label}</label>
              {f.textarea ? (
                <textarea className="input" rows={2} value={form[f.key] ?? ""} onChange={set(f.key)} data-testid={`stock-field-${f.key}`} />
              ) : f.kind === "typeahead" ? (
                <StockTypeahead field={f.key} value={form[f.key]} onChange={setVal(f.key)}
                  placeholder={f.placeholder} testid={`stock-field-${f.key}`} />
              ) : f.kind === "unit" ? (
                <select className="input" value={form.unit || ""} onChange={set("unit")} data-testid={`stock-field-${f.key}`}>
                  <option value="">— Select unit —</option>
                  {unitSelectOptions.map((u) => <option key={u} value={u}>{u === "EA" ? "EA — Each" : u}</option>)}
                </select>
              ) : f.kind === "currency" ? (
                <select className="input" value={form.currency || DEFAULT_CURRENCY} onChange={set("currency")} data-testid={`stock-field-${f.key}`}>
                  {currencySelectOptions.map(([code, name]) => <option key={code} value={code}>{code} — {name}</option>)}
                </select>
              ) : (
                <input className={`input ${f.type === "number" ? "mono" : ""}`} type={f.type || "text"} step={f.step}
                  value={form[f.key] ?? ""} onChange={set(f.key)} placeholder={f.placeholder} data-testid={`stock-field-${f.key}`} />
              )}
            </div>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn gold" onClick={save} disabled={busy} data-testid="stock-save">{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function ImportModal({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("append");
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { if (open) { setFile(null); setMode("append"); } }, [open]);
  if (!open) return null;

  const run = async () => {
    if (!file) return toast.error("Choose a CSV file first.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/stock-items/import?mode=${mode}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
      toast.success(`Imported ${r.data.imported} rows (${r.data.mode})`);
      onDone(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 210,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()} className="card pad" style={{ width: "min(520px,100%)", background: "var(--card)" }} data-testid="stock-import-modal">
        <h3 style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 8 }}><FileUp size={18} color="var(--gold-deep)" /> Import stock CSV</h3>
        <p className="t-mut" style={{ fontSize: 12.5 }}>Upload a NewStockList CSV (same column layout as the Caspio export).</p>
        <input type="file" accept=".csv,text/csv" className="input" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="stock-import-file" />
        <div style={{ display: "flex", gap: 16, margin: "12px 0", fontSize: 13 }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input type="radio" checked={mode === "append"} onChange={() => setMode("append")} data-testid="stock-import-append" /> Add to existing
          </label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input type="radio" checked={mode === "replace"} onChange={() => setMode("replace")} data-testid="stock-import-replace" /> Replace all
          </label>
        </div>
        {mode === "replace" && <div className="card pad" style={{ background: "#fff5f5", border: "1px solid #e3b0b0", fontSize: 12, marginBottom: 10 }}>Replace wipes the entire Stock Register before importing.</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn gold" onClick={run} disabled={busy || !file} data-testid="stock-import-run"><Upload size={14} /> {busy ? "Importing…" : "Import"}</button>
        </div>
      </div>
    </div>
  );
}

export default function StockRegister() {
  const { can } = useAuth();
  const canEdit = can("stock_register", "edit");
  const [refresh, setRefresh] = useState(0);
  const [modal, setModal] = useState(null); // { initial }
  const [importOpen, setImportOpen] = useState(false);

  const onDelete = async (row) => {
    if (!window.confirm(`Delete stock item "${row.part_number || row.description || row.id}"?\n\nThis permanently removes the line.`)) return;
    try {
      await api.delete(`/stock-items/${row.id}`);
      toast.success("Stock item deleted");
      setRefresh((x) => x + 1);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const txt = (v) => v || <span className="t-mut">—</span>;
  const columns = [
    { key: "part_number", label: "Part no.", sortable: true, colSearch: true,
      render: (v) => <span className="mono t-strong">{v || "—"}</span> },
    { key: "description", label: "Description", sortable: true, colSearch: true,
      render: (v) => <span className="t-strong">{v || "—"}</span> },
    { key: "keyword", label: "Keyword", sortable: true, colSearch: true, render: txt },
    { key: "qty", label: "Qty", sortable: true, render: (v) => <span className="mono">{v ?? 0}</span> },
    { key: "unit", label: "Unit", colSearch: true, render: (v) => <span className="mono">{v || "—"}</span> },
    { key: "price", label: "Price", sortable: true, render: (v) => <span className="mono">{v ? Number(v).toFixed(2) : "—"}</span> },
    { key: "currency", label: "Curr.", colSearch: true, render: (v) => <span className="mono">{v || "—"}</span> },
    { key: "supplier", label: "Supplier", sortable: true, colSearch: true, render: txt },
    { key: "bin", label: "Bin", sortable: true, colSearch: true, render: (v) => <span className="mono">{v || "—"}</span> },
    { key: "store", label: "Store", sortable: true, colSearch: true, render: txt },
    canEdit && {
      key: "_actions", label: "", sortable: false,
      render: (_, r) => (
        <span style={{ display: "inline-flex", gap: 4 }}>
          <button className="btn ghost sm" onClick={(e) => { e.stopPropagation(); setModal({ initial: r }); }} data-testid={`stock-edit-${r.id}`} title="Edit"><Edit2 size={12} /></button>
          <button className="btn ghost sm" onClick={(e) => { e.stopPropagation(); onDelete(r); }} data-testid={`stock-del-${r.id}`} title="Delete"><Trash2 size={12} color="var(--red)" /></button>
        </span>
      ),
    },
  ].filter(Boolean);

  return (
    <>
      <PageHead crumb="Records" title="Stock Register"
        sub="Internal parts & components stock list (imported from the Caspio NewStockList). Searchable and sortable — edit, delete, or add lines as stock changes." />
      <DataTable endpoint="/stock-items" columns={columns} rowKey="id" testid="stock-register"
        defaultSort={{ by: "part_number", dir: "asc" }} initialLimit={50}
        searchPlaceholder="Search part no., description, keyword, supplier, bin…"
        refreshSignal={refresh}
        rightToolbar={canEdit && (
          <>
            <button className="btn ghost" onClick={() => setImportOpen(true)} data-testid="stock-import-btn"><Upload size={13} /> Import CSV</button>
            <button className="btn gold" onClick={() => setModal({ initial: null })} data-testid="stock-add"><Plus size={13} /> Add stock item</button>
          </>
        )}
      />
      <StockModal open={!!modal} initial={modal?.initial} onClose={() => setModal(null)} onSaved={() => setRefresh((x) => x + 1)} />
      <ImportModal open={importOpen} onClose={() => setImportOpen(false)} onDone={() => setRefresh((x) => x + 1)} />
    </>
  );
}
